/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

let measurementCanvas;

/**
 * Measures the typographic advance width of a short text run. The fallback
 * keeps server rendering deterministic; browser output uses intrinsic layout.
 *
 * @param {string} content Text to measure.
 * @param {Object} options Font and fallback options.
 * @return {number} Width in CSS pixels.
 */
function measureTextWidth(
  content,
  {
    fallbackWidth,
    fontFamily,
    fontSize,
    fontStyle = 'normal',
    fontWeight = 400,
  }
) {
  if (
    typeof document === 'undefined' ||
    typeof CanvasRenderingContext2D === 'undefined'
  ) {
    return fallbackWidth;
  }

  try {
    if (!measurementCanvas) {
      measurementCanvas = document.createElement('canvas');
    }
    const context = measurementCanvas.getContext('2d');
    if (!context) {
      return fallbackWidth;
    }

    context.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
    const width = context.measureText(content).width;
    return Number.isFinite(width) && width > 0 ? width : fallbackWidth;
  } catch {
    return fallbackWidth;
  }
}

export default measureTextWidth;
